import { useMemo, memo } from 'react'
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'

type EmotionData = {
  emotion: string
  confidence: number
}

type PlutchikChartProps = {
  emotions: EmotionData[]
}

const PLUTCHIK_COLORS: Record<string, string> = {
  angry: '#ef4444',
  disgusted: '#8b5cf6',
  fearful: '#6366f1',
  happy: '#22c55e',
  neutral: '#6b7280',
  sad: '#3b82f6',
  surprised: '#f59e0b',
}

const PLUTCHIK_ORDER = ['happy', 'surprised', 'fearful', 'angry', 'disgusted', 'sad', 'neutral']

function PlutchikChartComponent({ emotions }: PlutchikChartProps) {
  const sortedEmotions = useMemo(() => {
    return [...emotions].sort((a, b) => {
      const aIndex = PLUTCHIK_ORDER.indexOf(a.emotion)
      const bIndex = PLUTCHIK_ORDER.indexOf(b.emotion)
      if (aIndex === -1 && bIndex === -1) return 0
      if (aIndex === -1) return 1
      if (bIndex === -1) return -1
      return aIndex - bIndex
    })
  }, [emotions])

  const data = useMemo(() => {
    return sortedEmotions.map((e) => ({
      name: e.emotion.charAt(0).toUpperCase() + e.emotion.slice(1),
      value: Number((e.confidence * 100).toFixed(1)),
      color: PLUTCHIK_COLORS[e.emotion] || '#6b7280',
    }))
  }, [sortedEmotions])

  if (!data || data.length === 0) {
    return <div style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>No data available</div>
  }

  const totalValue = data.reduce((sum, item) => sum + item.value, 0)
  if (totalValue === 0) {
    return <div style={{ textAlign: 'center', padding: 20, color: '#6b7280' }}>All values are zero</div>
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <ResponsiveContainer width="100%" height={300}>
        <RechartsPieChart margin={{ top: 0, right: 0, bottom: 40, left: 0 }}>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, value }) => {
              if (!value || value < 5) return ''
              return `${name}: ${value}%`
            }}
            outerRadius={80}
            innerRadius={40}
            fill="#8884d8"
            dataKey="value"
            startAngle={90}
            endAngle={-270}
            isAnimationActive={true}
            animationDuration={600}
            animationBegin={0}
            animationEasing="ease-out"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} stroke="#fff" strokeWidth={2} />
            ))}
          </Pie>
          <Tooltip formatter={(value: number) => `${value}%`} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }} />
          <Legend verticalAlign="bottom" height={36} />
        </RechartsPieChart>
      </ResponsiveContainer>
      <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: '#6b7280' }}>
        Plutchik Wheel of Emotions
      </div>
    </div>
  )
}

export default memo(PlutchikChartComponent, (prevProps, nextProps) => {
  if (prevProps.emotions.length !== nextProps.emotions.length) {
    return false
  }
  
  const prevMap = new Map(prevProps.emotions.map((e) => [e.emotion, e.confidence]))
  const nextMap = new Map(nextProps.emotions.map((e) => [e.emotion, e.confidence]))
  
  for (const emotion of prevProps.emotions) {
    const prevConf = prevMap.get(emotion.emotion) || 0
    const nextConf = nextMap.get(emotion.emotion) || 0
    if (Math.abs(prevConf - nextConf) > 0.001) {
      return false
    }
  }
  
  return true
})

